import React from "react";
import {
  Box,
  Heading,
  Text,
  Grid,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Calendar, Droplet, Mars, Mail, UserStar } from "lucide-react";
import { PatientOverview } from "@/types";

interface AboutCardProps {
  patient: PatientOverview;
  isMobile?: boolean;
}

export const PatientAboutCard: React.FC<AboutCardProps> = ({
  patient,
  isMobile,
}) => {
  return (
    <Box
      bg="white"
      borderRadius={{ base: "md", md: "lg" }}
      border="1px solid"
      borderColor="gray.200"
      p={{ base: 3, sm: 4, md: 6 }}
      shadow="sm"
    >
      <Heading
        size="sm"
        mb={4}
        display="flex"
        alignItems="center"
        gap={2}
        fontSize={{ base: "sm", md: "md" }}
      >
        <UserStar size={isMobile ? 14 : 16} />
        <Text>About</Text>
      </Heading>
      <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>
      <Grid
        templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
        gap={{ base: 4, sm: 6 }}
        fontSize="sm"
      >
        <Flex alignItems="start" gap={3}>
          <Box
            w={{ base: "30px", sm: "32px", md: "40px" }}
            h={{ base: "30px", sm: "32px", md: "40px" }}
            rounded="full"
            bg="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Calendar size={isMobile ? 16 : 18} />
          </Box>
          <Box>
            <Text
              fontSize={{ base: "xs", sm: "sm" }}
              color="gray.900"
              fontWeight="600"
              mb={1}
            >
              DOB
            </Text>
            <Text
              fontSize={{ base: "xs", sm: "sm" }}
              color="gray.500"
              fontWeight="400"
            >
              {patient.birthday || "N/A"}
            </Text>
          </Box>
        </Flex>
        <Flex alignItems="start" gap={3}>
          <Box
            w={{ base: "30px", sm: "32px", md: "40px" }}
            h={{ base: "30px", sm: "32px", md: "40px" }}
            rounded="full"
            bg="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Droplet size={isMobile ? 16 : 18} />
          </Box>
          <Box>
            <Text
              fontSize={{ base: "xs", sm: "sm" }}
              color="gray.900"
              fontWeight="600"
              mb={1}
            >
              Blood Type
            </Text>
            <Text
              fontSize={{ base: "xs", sm: "sm" }}
              fontWeight="400"
              color="gray.500"
            >
              {patient.blood_group || "N/A"}
            </Text>
          </Box>
        </Flex>
        <Flex alignItems="start" gap={3}>
          <Box
            w={{ base: "30px", sm: "32px", md: "40px" }}
            h={{ base: "30px", sm: "32px", md: "40px" }}
            rounded="full"
            bg="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Mars size={isMobile ? 16 : 18} />
          </Box>
          <Box>
            <Text
              fontSize={{ base: "xs", sm: "sm" }}
              color="gray.900"
              fontWeight="600"
              mb={1}
            >
              Gender
            </Text>
            <Text
              fontSize={{ base: "xs", sm: "sm" }}
              color="gray.500"
              fontWeight="400"
            >
              {patient.gender
                ? patient.gender.charAt(0).toUpperCase() +
                  patient.gender.slice(1)
                : "N/A"}
            </Text>
          </Box>
        </Flex>
        <Flex alignItems="start" gap={3}>
          <Box
            w={{ base: "30px", sm: "32px", md: "40px" }}
            h={{ base: "30px", sm: "32px", md: "40px" }}
            rounded="full"
            bg="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Mail size={isMobile ? 16 : 18} />
          </Box>
          <Box>
            <Text
              fontSize={{ base: "xs", sm: "sm" }}
              color="gray.900"
              fontWeight="600"
              mb={1}
            >
              Email
            </Text>
            <Text
              fontWeight="400"
              color="gray.500"
              fontSize={{ base: "xs", sm: "sm" }}
              wordBreak="break-all"
            >
              {patient.user_email}
            </Text>
          </Box>
        </Flex>
      </Grid>
    </Box>
  );
};
